import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Box, Grid } from '@material-ui/core';

import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { TruncatedText } from 'core/components/truncated-text';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import editIcon from 'core/assets/svg/edit.svg';
import { dateTimeFormatter } from 'core/helpers/formatters';

import useStyles from './document-preview-card.styles';

const DocumentPreviewCard = ({ doc, fileName, imageUrl, canRename, onRename }) => {
  const classes = useStyles();

  const displayName = doc.displayName || fileName;

  const openInNewTab = () => {
    window.open(imageUrl, '_blank');
  };

  return (
    <Card type="primary" className={classes.card}>
      <div className={classes.imageContainer} onClick={openInNewTab}>
        <img src={imageUrl} alt={displayName} className={classes.image} />
      </div>
      <div className={classes.content}>
        <Grid container alignItems="flex-start" className={classes.row}>
          <Grid item xs>
            <Box display="flex" flexDirection="column">
              <Label type="infoLabel" className={classes.rowLabel}>
                Name
              </Label>
              <TruncatedText maxLength={25} text={displayName} className={classes.rowText} />
              {doc.displayName && (
                <TruncatedText
                  maxLength={30}
                  text={fileName}
                  className={classNames(classes.rowText, classes.originalFileName)}
                />
              )}
            </Box>
          </Grid>
          <Grid item className={classes.actionIconContainer}>
            {canRename && (
              <IconButtonWithTooltip
                tooltip="Rename file"
                icon={editIcon}
                alt="Rename file"
                onClick={onRename}
                tooltipClassName={classes.tooltip}
                iconClassName={classes.actionIcon}
              />
            )}
            <IconButtonWithTooltip
              tooltip="Open image in new tab"
              icon={externalLinkIcon}
              alt="Open image in new tab"
              onClick={openInNewTab}
              tooltipClassName={classes.tooltip}
              iconClassName={classes.actionIcon}
            />
          </Grid>
        </Grid>
        {doc.policyId && (
          <Box display="flex" flexDirection="column" mt={1}>
            <Label type="infoLabel" className={classes.rowLabel}>
              Policy
            </Label>
            <Label type="body4" className={classes.rowText}>
              {doc.policyId}
            </Label>
          </Box>
        )}
        <Box display="flex" flexDirection="column" mt={1}>
          <Label type="infoLabel" className={classes.rowLabel}>
            Last update
          </Label>
          <Label type="body4" className={classes.rowText}>
            {dateTimeFormatter(doc.lastModified)}
          </Label>
        </Box>
      </div>
    </Card>
  );
};

DocumentPreviewCard.propTypes = {
  doc: PropTypes.shape({
    path: PropTypes.string.isRequired,
    lastModified: PropTypes.string,
    displayName: PropTypes.string,
    policyId: PropTypes.string
  }).isRequired,
  fileName: PropTypes.string.isRequired,
  imageUrl: PropTypes.string.isRequired,
  canRename: PropTypes.bool,
  onRename: PropTypes.func.isRequired
};

DocumentPreviewCard.defaultProps = {
  canRename: false
};

export default DocumentPreviewCard;
